<template>
	<PageWrapper>
		<VectorsMobileFiber />
		<VectorsAnimatedFiber
			:class="[inSpanish? 'lg:translate-y-65 xl:translate-y-40' : 'translate-y-40 xl:translate-y-0']"
			:length="2000"
		/>
		<SectionContentContainer
			class="lg:-mt-24"
		>
			<template #items>
				<LazySectionContentItem
					v-for="(item, index) in $tm('about.section-items-1')"
					:key="item"
					:copy="item"
					:reverse="false"
					:viewbox="ABOUT_SECTION_ITEMS_1[index]?.viewbox"
					:image="ABOUT_SECTION_ITEMS_1[index]?.image"
					:lines="ABOUT_SECTION_ITEMS_1[index]?.lines"
					:circles="ABOUT_SECTION_ITEMS_1[index]?.circles"
					:orange-title-first="ABOUT_SECTION_ITEMS_1[index]?.orangeTitleFirst"
					:lazy="index > 0"
					:ui="{
						container: 'sm:gap-0 lg:gap-16',
						features: 'hidden lg:block',
						title: 'text-4xl sm:text-5xl font-semibold',
						wrapper: 'lg:pr-16'
					}"
					image-classes="w-[70%] sm:w-full max-w-[280px] lg:shrink-0 lg:ml-auto lg:max-w-[320px] xl:max-w-[405px] mx-auto sm:mt-8 lg:mr-0 xl:mr-12 lg:ml-auto -mb-16 lg:mt-48 xl:mt-32 lg:mb-0"
					hydrate-on-visible
				/>
			</template>
		</SectionContentContainer>
		<div class="xl:w-2/3 mx-auto 2xl:w-1/2">
			<div class="flex flex-col lg:items-end lg:mt-22 xl:mt-0">
				<LazyVisibleSectionContentFeature
					v-for="feature in $tm('about.feature-items-1')"
					:key="feature"
					class="px-8 py-4 lg:hidden"
					:item="feature"
					hydrate-on-visible
				/>
				<LazyVisibleSectionContentFeature
					v-for="feature in $tm('about.feature-items-2')"
					:key="feature"
					class="px-8 py-4 lg:w-2/3"
					:item="feature"
					hydrate-on-visible
				/>
				<div class="flex w-full lg:w-2/3 justify-center lg:justify-start">
					<LazyAboutLogos class="max-w-xl px-6 py-12" />
				</div>
				<LazyVisibleSectionContentFeature
					v-for="feature in $tm('about.feature-items-3')"
					:key="feature"
					class="px-8 py-4 lg:w-2/3"
					:item="feature"
					hydrate-on-visible
				/>
			</div>
		</div>
		<LazyVisibleSectionContentItem
			v-for="(item, index) in $tm('about.section-items-2')"
			:key="item"
			:copy="item"
			:viewbox="ABOUT_SECTION_ITEMS_2[index]?.viewbox"
			:image="ABOUT_SECTION_ITEMS_2[index]?.image"
			:lines="ABOUT_SECTION_ITEMS_2[index]?.lines"
			:circles="ABOUT_SECTION_ITEMS_2[index]?.circles"
			:orange-title-first="ABOUT_SECTION_ITEMS_2[index]?.orangeTitleFirst"
			class="px-6 xl:px-16"
			hydrate-on-visible
		/>
		<div>
			<LazyVisibleTestimonials
				:tracking-location="ABOUT_PAGE_LOCATIONS.testimonials"
				hydrate-on-visible
			/>
			<LazyVisibleFaqs
				:faqs="faqs"
				:tracking-location="ABOUT_PAGE_LOCATIONS.faq"
				divider="both"
				hydrate-on-visible
			/>
			<LazyVisibleBlogCarousel
				:id="`about-${locale}`"
				:tracking-location="ABOUT_PAGE_LOCATIONS.blogCarousel"
				hydrate-on-visible
			/>
			<LazyBannersAddressChecker :tracking-location="ABOUT_PAGE_LOCATIONS.ctaBanner" />
		</div>
	</PageWrapper>
</template>

<script setup lang="ts">
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'
import {
	ABOUT_SECTION_ITEMS_1, ABOUT_SECTION_ITEMS_2
} from '@/constants/components/about'
import { ABOUT_PAGE_LOCATIONS } from '@/constants/ga4/tracking-ids/pages/about'
import {
	BASE_URL,
	COMPANY_NAME,
	COMPANY_PHONE,
	STORYBLOK_VERSION
} from '@/constants/index.js'
import {
	JSON_LD_HAS_OFFER_CATALOG,
	JSON_LD_LOGO,
	JSON_LD_ORGANIZATION_ADDRESS,
	JSON_LD_ORGANIZATION_CONTACT_POINT,
	JSON_LD_ORGANIZATION_REVIEW,
	JSON_LD_ORGANIZATION_SAME_AS
} from '@/constants/json-ld'
import { OG_IMAGE_DEFAULT } from '@/constants/og-image'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const route = useRoute()

const {
	t, tm, locale
} = useI18n({
	useScope: 'global'
})

// -----------------------
// ------ Page Data ------
// -----------------------

// Fetch storyblok data
const { getStory } = useStoryblokWithErrorReporting()
const storyblok = await getStory('about')
const { story } = storyblok.value?.data || {}
const storyblokApi = useStoryblokApi()
const { data } = await useAsyncData(
	`service-areas-${locale.value}`,
	async () => await storyblokApi.get('cdn/stories', {
		version: STORYBLOK_VERSION,
		starts_with: 'service-areas',
		is_startpage: 0,
		language: locale.value
	} as any)
)
if (!data.value || !data.value?.data?.stories?.length) {
	reportError('No service areas found')
}
const stories = computed(() => data?.value?.data?.stories)

// Get FAQs
const faqs = tm('faqs.general-faqs')

const inSpanish = computed(() => {
	return locale.value === 'es'
})

// -----------------
// ------ SEO ------
// -----------------
const areasJsonLd = computed(() => {
	return stories?.value?.map((story: any) => {
		const {
			city, stateShort
		} = story?.content?.cityCardContent?.[0] || {}
		return {
			'@type': 'Place',
			'address': {
				'@type': 'PostalAddress',
				'addressLocality': city,
				'addressRegion': stateShort
			},
			'potentialAction': {
				'@type': 'SearchAction',
				'query': `Internet services in ${city}, ${stateShort}`
			}
		}
	})
})

useHead({
	title: t('about.meta-title'),
	meta: [
		{ name: 'description',
			content: t('about.meta-description', { COMPANY_NAME }) },
		{ name: 'url',
			content: `${BASE_URL}/about` }
	],
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicons/favicon.ico'
		}
	]
})

const {
	title, description
} = story.content?.metatags || {
	title: t('about.meta-title'),
	description: t('about.meta-description', { COMPANY_NAME })
}

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogUrl: () => BASE_URL,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => '/images/og-image-fallback.png',
	twitterCard: () => 'summary_large_image'
})
 import.meta.prerender && defineOgImage('Default', OG_IMAGE_DEFAULT)

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'BreadcrumbList',
			'itemListElement': [
				{
					'@type': 'ListItem',
					'position': 1,
					'name': 'Home',
					'item': BASE_URL
				}, {
					'@type': 'ListItem',
					'position': 2,
					'name': 'About',
					'item': `${BASE_URL}${route.fullPath}`
				}
			]
		},
		{
			'@type': 'Organization',
			'name': COMPANY_NAME,
			'logo': JSON_LD_LOGO,
			'url': BASE_URL,
			'telephone': COMPANY_PHONE,
			'description': t('meta-description', { COMPANY_NAME }),
			'contactPoint': JSON_LD_ORGANIZATION_CONTACT_POINT,
			'sameAs': JSON_LD_ORGANIZATION_SAME_AS,
			'address': JSON_LD_ORGANIZATION_ADDRESS,
			'review': JSON_LD_ORGANIZATION_REVIEW,
			'areaServed': [
				...areasJsonLd.value
			],
			'hasOfferCatalog': JSON_LD_HAS_OFFER_CATALOG
		}
	]
})
</script>
